import React from 'react';
import Slideshow, { SlideContent } from '@/components/lectures/Slideshow';
import KeyTakeaway from '@/components/ui/key-takeaway';
import { AlertTriangle, Info, Activity, FileText, AlertCircle, Beaker, Pill, Shield, Check } from 'lucide-react';

interface PeptideSlideshowProps {
  // Basic information
  title: string;
  fullName: string;
  description: string[];
  diagram: React.ReactNode; // Component for the peptide diagram
  
  // Key takeaway
  keyTakeaway: string;
  
  // Mechanisms table data
  mechanismsTable: {
    headers: string[];
    rows: {
      activity: string;
      activityColor: string;
      mechanism: string;
      clinicalRelevance: string;
    }[];
  };
  
  // Cross-talk information
  crossTalk: {
    title: string;
    content: string;
  };
  
  // Clinical applications & dosing
  dosing: {
    title: string;
    protocols: {
      title: string;
      titleColor: string;
      steps: {
        number: number;
        label: string;
        content: string;
      }[];
    }[];
    additionalProtocol?: {
      title: string;
      titleColor: string;
      items: {
        number: number;
        label: string;
        content: string;
      }[][];
    };
  };
  
  // Storage information
  storage: {
    title: string;
    items: string[];
  };
  
  // Benefits and combinations
  benefits: {
    title: string;
    items: {
      title: string;
      content: string;
    }[];
  };
  
  // Any additional custom content at the end
  additionalContent?: React.ReactNode;
}

export default function PeptideSlideshow({
  title,
  fullName,
  description,
  diagram,
  keyTakeaway,
  mechanismsTable,
  crossTalk,
  dosing,
  storage,
  benefits,
  additionalContent
}: PeptideSlideshowProps) {
  
  // Convert the peptide module content into slides
  const slides: SlideContent[] = [
    // Slide 1: Overview
    {
      title: `${title} Overview`,
      content: (
        <div className="flex flex-col md:flex-row gap-6">
          <div className="flex-1">
            {description.map((paragraph, index) => (
              <p key={index} className={index < description.length - 1 ? "mb-4" : ""}>
                {paragraph}
              </p>
            ))}
            
            <div className="mt-6">
              <KeyTakeaway>
                {keyTakeaway}
              </KeyTakeaway>
            </div>
          </div>
          <div className="flex-1 flex items-center justify-center">
            {diagram}
          </div>
        </div>
      )
    },
    
    // Slide 2: Mechanisms
    {
      title: `${title} Mechanisms and Pathways`,
      content: (
        <div>
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm mb-6">
            <table className="w-full border-collapse bg-white text-sm">
              <thead>
                <tr>
                  {mechanismsTable.headers.map((header, index) => (
                    <th key={index} className="border-b border-gray-200 bg-gray-50 p-4 text-left font-medium text-gray-700">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {mechanismsTable.rows.map((row, index) => (
                  <tr key={index}>
                    <td className={`${index < mechanismsTable.rows.length - 1 ? 'border-b' : ''} border-gray-200 p-4 font-medium text-${row.activityColor}`}>
                      {row.activity}
                    </td>
                    <td className={`${index < mechanismsTable.rows.length - 1 ? 'border-b' : ''} border-gray-200 p-4 text-gray-700`}>
                      {row.mechanism}
                    </td>
                    <td className={`${index < mechanismsTable.rows.length - 1 ? 'border-b' : ''} border-gray-200 p-4 text-gray-700`}>
                      {row.clinicalRelevance}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="bg-blue-50 border-l-4 border-blue-500 p-5 rounded-r-lg">
            <h4 className="font-semibold text-blue-800 mb-2 flex items-center">
              <Info className="h-5 w-5 mr-2 text-blue-600" />
              {crossTalk.title}
            </h4>
            <p className="text-blue-900">
              {crossTalk.content}
            </p>
          </div>
        </div>
      )
    },
    
    // Slide 3: Dosing Protocols
    {
      title: `${title} Dosing Protocols`,
      content: (
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl shadow-md p-6">
          <h4 className="text-lg font-semibold text-indigo-800 mb-4 flex items-center">
            <Activity className="h-5 w-5 mr-2 text-indigo-600" />
            {dosing.title}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {dosing.protocols.map((protocol, index) => (
              <div key={index} className="bg-white rounded-lg p-4 shadow-sm">
                <h5 className={`font-medium text-${protocol.titleColor}-700 mb-2`}>{protocol.title}</h5>
                <ul className="space-y-2">
                  {protocol.steps.map((step) => (
                    <li key={step.number} className="flex items-start">
                      <span className={`bg-${protocol.titleColor}-100 text-${protocol.titleColor}-800 text-xs font-medium rounded-full w-5 h-5 flex items-center justify-center mt-0.5 mr-2`}>
                        {step.number}
                      </span>
                      <div>
                        <span className="font-semibold">{step.label}:</span> {step.content}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          
          {dosing.additionalProtocol && (
            <div className="mt-4 bg-white rounded-lg p-4 shadow-sm">
              <h5 className={`font-medium text-${dosing.additionalProtocol.titleColor}-700 mb-2`}>
                {dosing.additionalProtocol.title}
              </h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {dosing.additionalProtocol.items.map((column, colIndex) => (
                  <ul key={colIndex} className="space-y-2">
                    {column.map((item) => (
                      <li key={item.number} className="flex items-start">
                        <span className={`bg-${dosing.additionalProtocol?.titleColor}-100 text-${dosing.additionalProtocol?.titleColor}-800 text-xs font-medium rounded-full w-5 h-5 flex items-center justify-center mt-0.5 mr-2`}>
                          {item.number}
                        </span>
                        <div>
                          <span className="font-semibold">{item.label}:</span> {item.content}
                        </div>
                      </li>
                    ))}
                  </ul>
                ))}
              </div>
            </div>
          )}
        </div>
      )
    },
    
    // Slide 4: Storage and Handling
    {
      title: `${title} Storage and Handling`,
      content: (
        <div className="bg-gray-50 rounded-lg p-6">
          <h4 className="font-medium text-gray-700 mb-4 flex items-center">
            <FileText className="h-5 w-5 mr-2 text-gray-600" />
            {storage.title}
          </h4>
          <ul className="list-disc pl-5 mb-4 space-y-1 text-gray-700">
            {storage.items.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      )
    },
    
    // Slide 5: Benefits
    {
      title: `${title} Clinical Benefits and Combinations`,
      content: (
        <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
          <div className="bg-gradient-to-r from-green-500 to-green-600 px-4 py-3">
            <h4 className="text-white font-medium">{benefits.title}</h4>
          </div>
          <div className="p-4">
            <ul className="space-y-3">
              {benefits.items.map((item, index) => (
                <li key={index} className="flex items-start">
                  <div className="flex-shrink-0">
                    <Check className="h-5 w-5 text-green-500" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">{item.title}:</span> {item.content}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Additional content if provided */}
          {additionalContent && (
            <div className="mt-6">
              {additionalContent}
            </div>
          )}
        </div>
      )
    }
  ];
  
  return <Slideshow slides={slides} />;
}